import { UserButton, useUser } from '@clerk/clerk-react';
import { Menu, MessageSquare, LifeBuoy } from 'lucide-react';
import { LanguageSelector } from '@/components/ui';
import { Link } from 'react-router-dom';
import { useUnreadCount } from '@/hooks/useMessages';
import { useUnreadTicketCount } from '@/hooks/useSupport';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { usePersistProfileLanguage, useSyncProfileLanguageOnce } from '@/hooks/useProfileLanguage';

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user } = useUser();
  // Remember the chosen language for the e-mails the server sends.
  const persistLanguage = usePersistProfileLanguage();
  useSyncProfileLanguageOnce();
  const { data: unreadCount = 0 } = useUnreadCount(user?.id);
  const { data: unreadTicketCount = 0 } = useUnreadTicketCount();

  return (
    <header className="sticky top-0 z-50 h-14 md:h-16 bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl border-b border-gray-200/60 dark:border-white/[0.06]">
      <div className="flex h-full items-center justify-between px-3 md:px-5">

        {/* Left zone */}
        <div className="flex items-center gap-2">
          {/* Mobile menu */}
          <button
            className="md:hidden flex items-center justify-center h-9 w-9 rounded-xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] active:scale-95 transition-all duration-150"
            onClick={onMenuClick}
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" strokeWidth={2} />
          </button>

          {/* Mobile logo */}
          <Link to="/dashboard" className="flex items-center md:hidden">
            <img src="/favicon-original.png" alt="BookiApp" className="h-7 w-auto" />
          </Link>
        </div>

        {/* Right zone */}
        <div className="flex items-center gap-1 md:gap-1.5">
          {/* Language */}
          <LanguageSelector variant="compact" showSearch={true} onLanguageChanged={persistLanguage} />

          {/* Messages */}
          <Link to="/dashboard/messages" className="relative group">
            <div className="flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-xl text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 active:scale-95 transition-all duration-200 cursor-pointer">
              <MessageSquare className="h-[20px] w-[20px]" strokeWidth={2} />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-950 shadow-lg shadow-blue-500/30 animate-in zoom-in duration-200">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>

          {/* Notifications */}
          <NotificationBell landlordId={user?.id} />

          {/* Support */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-support-mode'))}
            className="relative group"
          >
            <div className="flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-xl text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 active:scale-95 transition-all duration-200 cursor-pointer">
              <LifeBuoy className="h-[20px] w-[20px]" strokeWidth={2} />
            </div>
            {unreadTicketCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-950 shadow-lg shadow-amber-500/30 animate-in zoom-in duration-200">
                {unreadTicketCount > 9 ? '9+' : unreadTicketCount}
              </span>
            )}
          </button>

          {/* Separator */}
          <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-white/[0.08] mx-1.5" />

          {/* User */}
          {user && (
            <div className="flex items-center gap-2.5">
              <div className="hidden lg:block text-right mr-0.5">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight truncate max-w-[160px]">
                  {user.emailAddresses[0]?.emailAddress}
                </p>
              </div>
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-blue-200 dark:hover:ring-blue-500/20 transition-all duration-200',
                  },
                }}
                // Account deletion only via /dashboard/account (30-day undo,
                // Stripe renewal turned off) — hide Clerk's own "Delete account".
                userProfileProps={{
                  appearance: { elements: { profileSection__danger: { display: 'none' } } },
                }}
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
